import * as React from "react";
import styled from "styled-components";
import { animated, useSpring } from "react-spring";
import { Link } from "../../shared/Link";
import BackIcon from "../../../assets/back_arrow.svg";
import { toREM, toVWMobile } from "../../../helpers/styles";
import { BREAKPOINTS } from "../../../helpers/theme";
import * as CommonHeaderTypes from "../types";

export type DrawerMenuItemViewProps = {
  onBackClick: () => void;
  subMenuItem?: CommonHeaderTypes.SubMenuItemWithOptions;
  open: boolean;
};

const clientSideWindow =
  typeof window !== "undefined"
    ? window
    : {
        innerWidth: 0,
      };

const DrawerMenuItemView = ({
  onBackClick,
  subMenuItem,
  open,
}: DrawerMenuItemViewProps) => {
  const styles = useSpring({
    right: open ? 0 : -clientSideWindow.innerWidth,
    config: {
      tension: 280,
      friction: 30,
      clamp: true,
    },
  });
  if (!subMenuItem) {
    return null;
  }

  return (
    <Container style={styles}>
      <BackButtonContainer onClick={onBackClick}>
        <BackImage src={BackIcon} alt={"Back"} />
        <BackText>Back</BackText>
      </BackButtonContainer>
      <SelectedOptionRow>
        <SelectedOptionLabel>{subMenuItem.label}</SelectedOptionLabel>
      </SelectedOptionRow>
      <Content>
        {subMenuItem.options?.map((option, optionIndex) => (
          <React.Fragment key={`option-${option.name}-${optionIndex}`}>
            {option?.name && <Title>{option.name}</Title>}
            {option?.items?.map((item, itemIndex) => {
              return (
                <Item key={`item-${item.name}-${itemIndex}`}>
                  <ImageContainer>
                    <ItemImage src={item?.image} alt={item?.imageAlt} />
                  </ImageContainer>
                  <ItemName
                    to={
                      item?.primaryButtonLink ||
                      (item as unknown as { menuItemLink?: string })
                        ?.menuItemLink
                    }
                  >
                    {item?.name || item?.primaryButtonLabel}
                  </ItemName>
                </Item>
              );
            })}
          </React.Fragment>
        ))}
      </Content>
    </Container>
  );
};

const Container = styled(animated.div)`
  width: 100vw;
  position: fixed;
  top: ${toVWMobile(70)}vw;
  right: -${clientSideWindow.innerWidth}px;
  bottom: 0;
  background-color: #f6f1e7;
  z-index: 101;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background-color: #012604;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    left: ${toVWMobile(15.75)}vw;
  }

  &::after {
    right: ${toVWMobile(15.75)}vw;
  }

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    display: none;
  }
`;

const BackButtonContainer = styled.button`
  width: 100%;
  min-height: ${toVWMobile(60)}vw;
  border: none;
  border-bottom: 1px solid #012604;
  padding: 0 ${toVWMobile(30)}vw;
  display: flex;
  flex-direction: ${(props) => (props.theme.isRTL ? "row-reverse" : "row")};
  justify-content: ${(props) =>
    props.theme.isRTL ? "flex-end" : "flex-start"};
  align-items: center;
  background-color: transparent;
  position: relative;
  z-index: 2;
`;

const BackImage = styled.img`
  object-fit: contain;
  width: ${toVWMobile(23)}vw;
  height: auto;
`;

const BackText = styled.span`
  font-family: "GT Alpina";
  font-size: ${toREM(18)}rem;
  margin-left: ${toVWMobile(12)}vw;
  font-weight: 300;
  line-height: normal;
  letter-spacing: ${toREM(-0.9)}rem;
  color: #012604;
`;

const Title = styled.div`
  font-family: "GT Alpina";
  font-size: ${toREM(40)}rem;
  font-style: normal;
  font-weight: 300;
  line-height: 0.9;
  letter-spacing: ${toREM(-2)}rem;
  color: #012604;
  text-align: center;
  max-width: ${toVWMobile(320)}vw;
  margin: 0 auto ${toVWMobile(32)}vw;
`;

const Content = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${toVWMobile(28)}vw ${toVWMobile(30)}vw ${toVWMobile(56)}vw;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
`;

const Item = styled.div`
  padding: 0 0 ${toVWMobile(56)}vw 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: ${toVWMobile(12)}vw;
`;

const ItemName = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: "GT Alpina";
  font-size: ${toREM(16)}rem;
  font-weight: 300;
  line-height: 1;
  letter-spacing: ${toREM(-0.64)}rem;
  color: #012604;
  text-decoration: none;
  background-color: #b5dd71;
  border-radius: ${toREM(999)}rem;
  height: ${toREM(35)}rem;
  padding: 0 ${toREM(32)}rem;
  text-align: center;
  white-space: nowrap;
`;

const SelectedOptionRow = styled.div`
  width: 100%;
  min-height: ${toVWMobile(70)}vw;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #012604;
  padding: 0 ${toVWMobile(30)}vw;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
`;

const SelectedOptionLabel = styled.div`
  font-family: "GT Alpina";
  font-size: ${toREM(32)}rem;
  font-style: normal;
  font-weight: 300;
  line-height: 0.9;
  letter-spacing: ${toREM(-1.6)}rem;
  color: #012604;
`;

const ImageContainer = styled.div`
  height: ${toREM(200)}rem;
  width: ${toREM(274)}rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

export default DrawerMenuItemView;
